import React, { useState } from 'react';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import { TOTEM_CLOSED_DAY_TYPE_LABELS } from '../constants/types';

import { TotemPopup } from 'components/TotemPopup';
import { TotemPrimaryLoadingButton } from 'components/TotemPrimaryLoadingButton';
import { TotemSelect } from 'components/TotemSelect';
import { TotemDatePicker } from 'components/TotemDatePicker';

import { TotemClosedDayType, useCreateTotemClosedDayMutation } from 'data/__generated__';

import { formatDateAsAnniversary } from 'helpers/dateTimes';
import { totemClosedDayCreatedHandler } from '../cacheHandlers/totemClosedDayCacheHandler';

const TYPE_OPTIONS = Object.values(TotemClosedDayType).map((type: TotemClosedDayType) => ({
    label: TOTEM_CLOSED_DAY_TYPE_LABELS[type],
    value: type,
}));

export function CreateTotemClosedDayPopup({
    isOpen,
    setIsOpen,
}: {
    isOpen: boolean;
    setIsOpen: (arg: boolean) => void;
}) {
    const [type, setType] = useState<TotemClosedDayType>(TotemClosedDayType.BankHoliday);
    const [date, setDate] = useState<Date | null>(null);

    const [createTotemClosedDay, { loading }] = useCreateTotemClosedDayMutation();

    async function handleSubmit() {
        if (!date || !type) {
            return;
        }

        const { data } = await createTotemClosedDay({
            variables: {
                createTotemClosedDayInput: {
                    date: formatDateAsAnniversary({ dateTime: date, useNewFormat: true }),
                    type,
                },
            },
        });

        if (!data) {
            throw new Error('Une erreur est survenue lors de la création du jour de fermeture');
        }

        const { createTotemClosedDay: newTotemClosedDay } = data;

        if (newTotemClosedDay) {
            totemClosedDayCreatedHandler(newTotemClosedDay);
            toast.success('Jour de fermeture créé !');
            setIsOpen(false);
        } else {
            toast.error('Une erreur est survenue');
        }
    }

    return (
        <TotemPopup title="Créer un jour de fermeture" isOpen={isOpen} setIsOpen={setIsOpen} contentOverflow="visible">
            <WarningMessage>
                Si le jour de fermeture est un jour férié, alors celui-ci sera automatiquement ajouté comme date de
                fermeture à chacun des sites.
                <br />
                Tout autre type de jour de fermeture n'est pour le moment pas rajouté automatiquement aux sites.
                <br />
                <br />
                Attention, supprimer un jour férié des jours de fermetures Totem ne le retire pas automatiquement des
                sites. Il faut le faire à la main ou faire un tech support.
            </WarningMessage>
            <SelectorContainer>
                <TotemSelect
                    label="Type de fermeture"
                    placeholder="Sélectionner un type"
                    value={type}
                    options={TYPE_OPTIONS}
                    onChange={(value) => {
                        if (value) {
                            setType(value);
                        }
                    }}
                />
            </SelectorContainer>
            <SelectorContainer>
                <TotemDatePicker
                    label="Date"
                    selected={date}
                    onChange={(date) => {
                        setDate(date as Date);
                    }}
                />
            </SelectorContainer>

            <SubmitContainer>
                <TotemPrimaryLoadingButton
                    loading={loading}
                    disabled={!date || !type}
                    onClick={handleSubmit}
                    useIcon={false}
                    label="Créer le jour de fermeture"
                />
            </SubmitContainer>
        </TotemPopup>
    );
}

const SelectorContainer = styled.div`
    margin: 10px 0px;
`;

const SubmitContainer = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 15px;
`;

const WarningMessage = styled.div`
    color: ${({ theme }) => theme.warningColor};
`;
